import React, { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ColumnReferenceCell,
  ColumnReferenceColumnCell,
  ColumnReferenceHead,
  ColumnReferenceTable,
  ExampleTableCell,
  ExampleTableHead,
  ExampleTableRow,
  GuideBackdrop,
  GuideBody,
  GuideCloseButton,
  GuideFooter,
  GuideHeader,
  GuideKickerLabel,
  GuideKickerRow,
  GuideModal,
  GuidePrimaryButton,
  GuideSubtitle,
  GuideSurveyBadge,
  GuideTitle,
  InfoCallout,
  InfoCalloutContent,
  InfoCalloutIcon,
  InfoCalloutList,
  InfoCalloutText,
  InfoCalloutTitle,
  MoreButton,
  MoreInlineCode,
  MorePanel,
  ParticipantsExampleTable,
  ParticipantsGuideSection,
  ParticipantsSectionTitle,
  RequiredYes,
  SectionDescription,
  VisualLabel,
} from './ImportModal.styles';
import CheckMark from '@/assets/icons/checkMark.svg?react';
import CloseMark from '@/assets/icons/closeMark.svg?react';

interface IImportParticipantsAdaptPulseHelpModal {
  active: boolean;
  onClose: () => void;
}

const COLUMN_ROW_IDS = [
  'email',
  'departments',
  'name',
  'phone',
  'language',
  'leader',
  'team',
  'role',
  'individualReportGroup',
  'backgroundVariables',
  'personalizations',
] as const;

const FILE_EXAMPLE_ROW_IDS = ['jane', 'john', 'lisa'] as const;

const FILE_EXAMPLE_FIELDS = [
  'email',
  'name',
  'department1',
  'department2',
  'team',
  'role',
  'language',
] as const;

const INFO_CALLOUT_IDS = ['emailKey', 'skipRow', 'departments'] as const;

type RequiredType = 'yes' | 'no' | 'atLeastOne';

const REQUIRED_TYPES: Record<typeof COLUMN_ROW_IDS[number], RequiredType> = {
  email: 'yes',
  departments: 'atLeastOne',
  name: 'no',
  phone: 'no',
  language: 'no',
  leader: 'no',
  team: 'no',
  role: 'no',
  individualReportGroup: 'no',
  backgroundVariables: 'no',
  personalizations: 'no',
};

const ImportParticipantsAdaptPulseHelpModal: FC<IImportParticipantsAdaptPulseHelpModal> = ({
  active,
  onClose,
}) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);

  const renderRequired = (requiredType: RequiredType) => {
    if (requiredType === 'yes') {
      return (
        <RequiredYes>
          {t('imports.participantsHelp.adaptPulse.columnReference.requiredYes')}
        </RequiredYes>
      );
    }

    if (requiredType === 'atLeastOne') {
      return t('imports.participantsHelp.adaptPulse.columnReference.requiredAtLeastOne');
    }

    return t('imports.participantsHelp.adaptPulse.columnReference.requiredNo');
  };

  const renderExampleCell = (rowId: typeof FILE_EXAMPLE_ROW_IDS[number], field: string) =>
    t(`imports.participantsHelp.adaptPulse.fileExample.rows.${rowId}.${field}`, {
      defaultValue: '',
    });

  if (!active) return null;

  return (
    <>
      <GuideBackdrop onClick={onClose} />
      <GuideModal
        role="dialog"
        aria-modal="true"
        aria-labelledby="participants-adapt-pulse-guide-title"
        data-testid="import-participants-adapt-pulse-help-content"
      >
        <GuideHeader>
          <GuideKickerRow>
            <GuideKickerLabel>{t('imports.participantsHelp.adaptPulse.kicker')}</GuideKickerLabel>
            <GuideSurveyBadge>{t('imports.participantsHelp.adaptPulse.badge')}</GuideSurveyBadge>
          </GuideKickerRow>
          <GuideCloseButton type="button" aria-label={t('imports.close')} onClick={onClose}>
            <CloseMark />
          </GuideCloseButton>
          <GuideTitle id="participants-adapt-pulse-guide-title">
            {t('imports.participantsHelp.adaptPulse.guideTitle')}
          </GuideTitle>
          <GuideSubtitle>{t('imports.participantsHelp.adaptPulse.guideSubtitle')}</GuideSubtitle>
        </GuideHeader>

        <GuideBody>
          <ParticipantsGuideSection>
            <ParticipantsSectionTitle>
              {t('imports.participantsHelp.adaptPulse.columnReference.title')}
            </ParticipantsSectionTitle>
            <SectionDescription>
              <Trans
                i18nKey="imports.participantsHelp.adaptPulse.columnReference.intro"
                components={[<MoreInlineCode key="email" />, <MoreInlineCode key="departmentN" />]}
              />
            </SectionDescription>

            <ColumnReferenceTable>
              <thead>
                <tr>
                  <ColumnReferenceHead>
                    {t('imports.participantsHelp.adaptPulse.columnReference.headers.column')}
                  </ColumnReferenceHead>
                  <ColumnReferenceHead>
                    {t('imports.participantsHelp.adaptPulse.columnReference.headers.required')}
                  </ColumnReferenceHead>
                  <ColumnReferenceHead>
                    {t('imports.participantsHelp.adaptPulse.columnReference.headers.acceptedNames')}
                  </ColumnReferenceHead>
                  <ColumnReferenceHead>
                    {t('imports.participantsHelp.adaptPulse.columnReference.headers.notes')}
                  </ColumnReferenceHead>
                </tr>
              </thead>
              <tbody>
                {COLUMN_ROW_IDS.map(rowId => (
                  <tr key={rowId}>
                    <ColumnReferenceColumnCell>
                      {t(
                        `imports.participantsHelp.adaptPulse.columnReference.rows.${rowId}.column`
                      )}
                    </ColumnReferenceColumnCell>
                    <ColumnReferenceCell>
                      {renderRequired(REQUIRED_TYPES[rowId])}
                    </ColumnReferenceCell>
                    <ColumnReferenceCell>
                      {t(
                        `imports.participantsHelp.adaptPulse.columnReference.rows.${rowId}.acceptedNames`
                      )}
                    </ColumnReferenceCell>
                    <ColumnReferenceCell>
                      {t(
                        `imports.participantsHelp.adaptPulse.columnReference.rows.${rowId}.notes`,
                        {
                          defaultValue: '',
                        }
                      )}
                    </ColumnReferenceCell>
                  </tr>
                ))}
              </tbody>
            </ColumnReferenceTable>

            <VisualLabel>{t('imports.participantsHelp.adaptPulse.fileExample.title')}</VisualLabel>
            <ParticipantsExampleTable>
              <thead>
                <ExampleTableRow>
                  {FILE_EXAMPLE_FIELDS.map(field => (
                    <ExampleTableHead key={field}>
                      {t(`imports.participantsHelp.adaptPulse.fileExample.headers.${field}`)}
                    </ExampleTableHead>
                  ))}
                </ExampleTableRow>
              </thead>
              <tbody>
                {FILE_EXAMPLE_ROW_IDS.map(rowId => (
                  <ExampleTableRow key={rowId}>
                    {FILE_EXAMPLE_FIELDS.map(field => (
                      <ExampleTableCell key={`${rowId}-${field}`}>
                        {renderExampleCell(rowId, field)}
                      </ExampleTableCell>
                    ))}
                  </ExampleTableRow>
                ))}
              </tbody>
            </ParticipantsExampleTable>

            <MoreButton
              type="button"
              aria-expanded={showMore}
              onClick={() => setShowMore(value => !value)}
            >
              <span>{showMore ? '−' : '+'}</span>
              {showMore
                ? t('imports.reportingGroupsHelp.hide')
                : t('imports.participantsHelp.adaptPulse.showMore')}
            </MoreButton>
            {showMore ? (
              <MorePanel>
                <InfoCalloutList>
                  {INFO_CALLOUT_IDS.map(calloutId => (
                    <InfoCallout key={calloutId}>
                      <InfoCalloutIcon aria-hidden="true">
                        <CheckMark />
                      </InfoCalloutIcon>
                      <InfoCalloutContent>
                        <InfoCalloutTitle>
                          {t(`imports.participantsHelp.adaptPulse.more.${calloutId}.title`)}
                        </InfoCalloutTitle>
                        <InfoCalloutText>
                          {t(`imports.participantsHelp.adaptPulse.more.${calloutId}.text`)}
                        </InfoCalloutText>
                      </InfoCalloutContent>
                    </InfoCallout>
                  ))}
                </InfoCalloutList>
              </MorePanel>
            ) : null}
          </ParticipantsGuideSection>
        </GuideBody>

        <GuideFooter>
          <GuidePrimaryButton type="button" onClick={onClose}>
            {t('imports.reportingGroupsHelp.gotIt')}
          </GuidePrimaryButton>
        </GuideFooter>
      </GuideModal>
    </>
  );
};

export default ImportParticipantsAdaptPulseHelpModal;
